import { useEffect, useState } from "react";
import {
  Grid, Paper, Typography, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, Tabs, Tab, TextField,
  TableFooter, TablePagination, Container, MenuItem, Select,
  FormControl, InputLabel, Button
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, startOfMonth, endOfMonth } from "date-fns";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  API_STUDENT_FEE_REPORTS,
  API_MISC_FEE_REPORTS,
} from "../Config/ApiUrlConstant";
import { useAuth } from "../AuthContext";

const formatColumnHeader = (col) =>
  col.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

const ReportTable = ({
  title,
  data,
  columns,
  page,
  rowsPerPage,
  setPage,
  setRowsPerPage,
}) => {
  const totals = {};
  columns.forEach((col) => {
    if (data.length && !isNaN(data[0][col])) {
      totals[col] = data.reduce((sum, row) => sum + Number(row[col] || 0), 0);
    }
  });

  // ✅ Export to Excel function
  const exportToExcel = () => {
    const worksheetData = data.map((row) => {
      const obj = {};
      columns.forEach((col) => {
        obj[formatColumnHeader(col)] = row[col];
      });
      return obj;
    });

    // Add totals row
    const totalsRow = {};
    columns.forEach((col) => {
      totalsRow[formatColumnHeader(col)] =
        totals[col] !== undefined ? totals[col] : "";
    });
    worksheetData.push(totalsRow);

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, title);

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), `${title}.xlsx`);
  };

  return (
    <Paper sx={{ p: 2, mb: 4 }}>
      <Grid container justifyContent="space-between"  >
        <Typography variant="h6" style={{float:"left"}}>{title}</Typography>
        <Button variant="contained" onClick={exportToExcel} style={{float:"right",width:'200px'}} > 
          Export to Excel
        </Button>
      </Grid>

      <TableContainer sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col}>{formatColumnHeader(col)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, i) => (
                <TableRow key={i}>
                  {columns.map((col) => (
                    <TableCell key={col}>{row[col]}</TableCell>
                  ))}
                </TableRow>
              ))}
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col} sx={{ fontWeight: "bold" }}>
                  {totals[col] !== undefined ? totals[col] : ""}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                count={data.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default function AllFeeReport() {
  const { logout } = useAuth();

  const today = new Date();
  const defaultSessionStart =
    today.getMonth() + 1 >= 4
      ? new Date(today.getFullYear(), 3, 1)
      : new Date(today.getFullYear() - 1, 3, 1);
  const defaultSessionEnd = new Date(today.getFullYear(), today.getMonth(), 1);

  const sessionOptions = ["2025-26", "2024-25", "2023-24"];

  const [tabIndex, setTabIndex] = useState(0);
  const [feeReport, setFeeReport] = useState([]);
  const [miscReport, setMiscReport] = useState([]);
  const [searchStudent, setSearchStudent] = useState("");
  const [searchFather, setSearchFather] = useState("");
  const [sessionStart, setSessionStart] = useState(defaultSessionStart);
  const [sessionEnd, setSessionEnd] = useState(defaultSessionEnd);
  const [paymentStatus, setPaymentStatus] = useState("All");
  const [selectedSession, setSelectedSession] = useState(sessionOptions[0]);

  const [pageFee, setPageFee] = useState(0);
  const [rowsPerPageFee, setRowsPerPageFee] = useState(10);
  const [pageMisc, setPageMisc] = useState(0);
  const [rowsPerPageMisc, setRowsPerPageMisc] = useState(10);

  useEffect(() => {
    if (sessionStart && sessionEnd && paymentStatus) {
      getPaidFeesReport();
    }
  }, [sessionStart, sessionEnd, paymentStatus]);

  useEffect(() => {
    if (selectedSession && paymentStatus) {
      getPaidMiscFeesReport();
    }
  }, [selectedSession, paymentStatus]);

  const getPaidFeesReport = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_STUDENT_FEE_REPORTS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          session_start: format(startOfMonth(sessionStart), "yyyy-MM-dd"),
          session_end: format(endOfMonth(sessionEnd), "yyyy-MM-dd"),
          status_flag: paymentStatus.toLowerCase(),
        }),
      });
      if ([401, 403].includes(response.status)) {
        logout();
        return;
      }
      const responseData = await response.json();
      setFeeReport(responseData.data || []);
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  const getPaidMiscFeesReport = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_MISC_FEE_REPORTS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          session: selectedSession,
        }),
      });
      if ([401, 403].includes(response.status)) {
        logout();
        return;
      }
      const responseData = await response.json();
      setMiscReport(responseData.data || []);
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  const handleTabChange = (event, newValue) => setTabIndex(newValue);

  const filteredFeeReport = feeReport.filter(
    (row) =>
      row.student_name?.toLowerCase().includes(searchStudent.toLowerCase()) &&
      row.student_father_name?.toLowerCase().includes(searchFather.toLowerCase())
  );

  const filteredMiscReport = miscReport.filter(
    (row) =>
      row.student_name?.toLowerCase().includes(searchStudent.toLowerCase()) &&
      row.student_father_name?.toLowerCase().includes(searchFather.toLowerCase())
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper sx={{ mb: 3, p: 2 }}>
        <Tabs value={tabIndex} onChange={handleTabChange} centered variant="fullWidth">
          <Tab label="All Student Fee Report" />
          <Tab label="All Miscellaneous Report" />
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Search Student Name"
              value={searchStudent}
              onChange={(e) => setSearchStudent(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Search Father Name"
              value={searchFather}
              onChange={(e) => setSearchFather(e.target.value)}
              fullWidth
            />
          </Grid>

          {tabIndex === 0 ? (
            <>
              <Grid item xs={12} sm={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Session Start"
                    views={["year", "month"]}
                    value={sessionStart}
                    onChange={(newValue) => setSessionStart(newValue)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Session End"
                    views={["year", "month"]}
                    value={sessionEnd}
                    onChange={(newValue) => setSessionEnd(newValue)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={paymentStatus}
                    onChange={(e) => setPaymentStatus(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Paid">Paid</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Session</InputLabel>
                  <Select
                    value={selectedSession}
                    onChange={(e) => setSelectedSession(e.target.value)}
                  >
                    {sessionOptions.map((session) => (
                      <MenuItem key={session} value={session}>
                        {session}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={paymentStatus}
                    onChange={(e) => setPaymentStatus(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Paid">Paid</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Tables */}
      {tabIndex === 0 && (
        <ReportTable
          title="All Fee Report"
          columns={[
            "student_no",
            "student_name",
            "student_father_name",
            "fee_month",
            "tuition_charge",
            "magic_charge",
            "fee_status",
          ]}
          data={filteredFeeReport}
          page={pageFee}
          rowsPerPage={rowsPerPageFee}
          setPage={setPageFee}
          setRowsPerPage={setRowsPerPageFee}
        />
      )}

      {tabIndex === 1 && (
        <ReportTable
          title="All Miscellaneous Report"
          columns={[
            "student_no",
            "student_name",
            "student_father_name",
            "book_paid_amt",
            "lower_tshirt_paid_amt",
            "annual_promotion_amt",
            "admission_amt",
            "diary_id_tie_belt_amt",
            "discount",
            "all_paid",
            "misc_pending",
          ]}
          data={filteredMiscReport}
          page={pageMisc}
          rowsPerPage={rowsPerPageMisc}
          setPage={setPageMisc}
          setRowsPerPage={setRowsPerPageMisc}
        />
      )}
    </Container>
  );
}
